import * as React from "react";
import { useEffect, useState } from "react";
import styled from "@mui/material/styles/styled";
import { DataInputPanel } from "../../dataInputTypes";
import ListItem from "@mui/material/ListItem";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { PatientChartFinding } from "./PatientChartFinding";
import { KahunPanelSubGroup, Relations, Values } from "shared";
import { QualifiedFeature, QualifiedFeatureFragment } from "fe-common/graphql";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import CheckIcon from "@mui/icons-material/Check";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { ActionButton } from "fe-common/ui/buttons/actionButton";
import { MobileOnly } from "../util/mobileOnly";
import { AgentTooltip } from "fe-common/ui/AgentTooltip";
import { DesktopOnly } from "../util/desktopOnly";
import EditIcon from "@mui/icons-material/Edit";
import EditIconSVG from "../../../assets/editIcon.svg";
import { IconButton } from "@mui/material";

//region [[ Styles ]]

const PatientChartProcedurePanelView = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginBottom: "10px",
  [theme.breakpoints.down(MobileView)]: {
    marginBottom: "0px",
  },
}));

const PatientChartProcedurePanelItem = styled(ListItem)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  [theme.breakpoints.down(MobileView)]: {
    paddingLeft: "0px",
    paddingRight: "0px",
  },

  "& .edit-button": {
    borderRadius: "5px",
    padding: "2px",
    height: "22px",
    fontSize: "12px",
    borderColor: KahunColorMap.PrimaryDim,
    color: KahunColorMap.PrimaryDim,
  },
}));

const ShowAllText = styled("div")({
  textDecoration: "underline",
  fontWeight: 500,
  fontSize: "14px",
  color: RosebudColorMap.gray2,
});

const PanelTitle = styled("div")(({ theme }) => ({
  fontSize: "16px",
  display: "flex",

  [theme.breakpoints.down(MobileView)]: {
    fontSize: "14px",
    color: KahunColorMap.PrimaryDim,
  },
}));

const CheckIconStyled = styled(CheckIcon)({
  fontSize: "16px",
  marginTop: "4px",
  color: "green",
});

const LeftArea = styled("div")({
  display: "flex",
  alignItems: "center",
  flexDirection: "row",
});

const EditIconContainer = styled(IconButton)({
  color: KahunColorMap.PrimaryDim,
});

const PanelTitleText = styled("div")({
  marginRight: "5px",
});

const DesktopOnlyStyled = styled(DesktopOnly)({
  width: "auto",
});

const ArrowContainer = styled("div")(({ expanded }: { expanded: string }) => ({
  transform: `rotate(${expanded === "true" ? "90" : "0"}deg)`,
  transition: "transform 0.3s ease-in",
  display: "flex",
  marginLeft: "-8px",
  alignItems: "center",
  justifyContent: "center",
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientChartProcedurePanelProps {
  panel: DataInputPanel;
  findings: QualifiedFeature[];
  subGroupId: string;
  onRemove: (f: any) => void;
  showAll: boolean;
  gotoSection: (subGroupId: any) => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const PatientChartProcedurePanel = ({
  ...props
}: PatientChartProcedurePanelProps) => {
  const findingsNotNormal = props.findings
    ? props.findings.filter(
        (finding) =>
          finding.qualifiers.filter(
            (q) =>
              q.relation.id === Relations.LEVEL &&
              q.value &&
              q.value.id !== Values.NORMAL,
          ).length > 0 || finding.negate === false,
      )
    : [];

  const findingsNormal = props.findings
    ? props.findings.filter(
        (finding) =>
          finding.qualifiers.filter(
            (q) =>
              q.relation.id === Relations.LEVEL &&
              q.value &&
              q.value.id === Values.NORMAL,
          ).length > 0 || finding.negate === true,
      )
    : [];

  const isProcedureNegative = props.findings.every(
    (finding) => finding.negate === true,
  );

  const isLevelLabTest = (finding: QualifiedFeatureFragment) => {
    if (
      finding.qualifiers.filter((q) => q.relation.id === Relations.LEVEL)
        .length > 0
    ) {
      return true;
    } else {
      return false;
    }
  };

  const isPositiveNegativeTest = (finding: QualifiedFeatureFragment) => {
    if (finding.negate === true || finding.negate === false) {
      return true;
    } else {
      return false;
    }
  };

  const [expanded, setExpanded] = useState(true);
  const [showMore, setShowMore] = useState(props.showAll);

  useEffect(() => {
    setShowMore(props.showAll);
    setExpanded(true);
  }, [props.showAll]);

  const showAllBottom = true;

  const findings =
    showMore || !props.panel
      ? [...findingsNotNormal, ...findingsNormal]
      : findingsNotNormal;

  const filteredFindings = findings?.filter(
    (f) => isLevelLabTest(f) || isPositiveNegativeTest(f),
  );

  const isLabsPanel = props.subGroupId === KahunPanelSubGroup.Labs;

  const onPanelClicked = () => {
    setExpanded(!expanded);

    /*setEditPanelPopupProps({
      open: true,
      patientCaseGrouping: PatientCaseGrouping.CLINICAL_FEATURES,
      panelContext: {
        panel: props.panel,
        caseGrouping: PatientCaseGrouping.WORKUP,
        panelCategory: props.panel.panelSubgroup,
      },
    });*/

    props.gotoSection(props.panel.id);
  };

  const onArrowClicked = (event) => {
    event.stopPropagation();
    setExpanded(!expanded);
  };

  const onDelete = (event: any, finding: QualifiedFeatureFragment) => {
    event.stopPropagation();
    evt({
      category: CATEGORY.AGENT_PATIENT_VIEW,
      action: ACTION.REMOVE_FINDING_CHIP,
      label: finding.name,
    });
    props.onRemove(finding);
  };

  const onShowAll = () => {
    setShowMore(!showMore);
  };

  const onEdit = (event) => {
    event.stopPropagation();
    if (props.panel !== null) {
      props.gotoSection(props.panel.id);
    } else {
      props.gotoSection(KahunPanelSubGroup.Other);
    }
  };

  return (
    <PatientChartProcedurePanelView>
      {props.panel && (
        <PatientChartProcedurePanelItem onClick={onPanelClicked}>
          <LeftArea>
            <ArrowContainer
              expanded={expanded ? "true" : "false"}
              onClick={onArrowClicked}
            >
              <ArrowRightIcon />
            </ArrowContainer>
            <PanelTitle>
              <PanelTitleText>{` ${props.panel.title}`}</PanelTitleText>
              {isProcedureNegative && <CheckIconStyled />}
            </PanelTitle>
          </LeftArea>

          {!showAllBottom &&
            findingsNotNormal.length > 0 &&
            findingsNotNormal.length !== props.findings.length && (
              <ShowAllText onClick={onShowAll}>
                {!expanded ? "Show All" : "Show Only Abnormal"}
              </ShowAllText>
            )}

          <DesktopOnlyStyled>
            <EditIconContainer size={"small"} onClick={onEdit}>
              <AgentTooltip title={"Edit"}>
                <EditIcon fontSize={"small"} />
              </AgentTooltip>
            </EditIconContainer>
          </DesktopOnlyStyled>

          <MobileOnly>
            <ActionButton
              onClick={onEdit}
              icon={<img src={EditIconSVG} alt={"Edit"} />}
              label={"Edit"}
              variant={"outlined"}
              className={"edit-button"}
            />
          </MobileOnly>
        </PatientChartProcedurePanelItem>
      )}

      {expanded &&
        filteredFindings &&
        filteredFindings.map((finding, index) => (
          <PatientChartFinding
            indented={props.panel !== null}
            key={index}
            qf={finding}
            gotoSection={props.gotoSection}
            index={index}
            isEditable={isLabsPanel ? true : false}
            onRefine={props.panel === null && isLabsPanel ? onEdit : null}
            onDelete={(event) => {
              onDelete(event, finding);
            }}
          />
        ))}
    </PatientChartProcedurePanelView>
  );
};
